/**
 * The Month Flow Excel export.
 *
 * LAID OUT LIKE THE OWNER'S OWN BILL SHEET, because that's the sheet they
 * already read fluently. That means, deliberately and not by accident:
 *   · the first four columns are theirs, in their order, with their headers —
 *     'Bill:', 'Amount:', 'Date:', 'Potentially:'
 *   · a spacer column between every data column (their A / C / E / G rhythm)
 *   · a blank row between every entry
 *   · real =SUM() formulas at the bottom, as theirs had, so editing an amount
 *     updates the total instead of leaving a stale number
 *
 * Everything beyond those four columns is the additional information the app
 * knows and the spreadsheet never did: what it works out to per month, what
 * actually falls in this period, when it next lands, which books it belongs
 * to, and where it came from.
 *
 * The three groups the owner thinks in — personal expenses, business expenses,
 * recurring income — each get a banner and a subtotal, then one grand total.
 *
 * DELIBERATELY UNFILTERED. The on-screen chips narrow what you're looking at;
 * this is the complete picture for the chosen range. A spreadsheet that quietly
 * dropped half the bills because a chip was toggled would be wrong in a way
 * nobody could see. Switched-off and muted items are included too, marked in a
 * Status column, so the totals always reconcile against the app. The CSV
 * exports remain the way to take a copy of exactly what's on screen.
 */

import { RepeatingInvoice } from '../../../types/accounting';
import { CrmSubscription } from '../../../contexts/SubscriptionsContext';
import {
  MoneySource,
  RecurringMoneyItem,
  RhythmOccurrence,
  RhythmScenario,
} from '../types';
import { getCategoryLabel } from './categories';
import {
  SCOPE_LABELS,
  SOURCE_LABELS,
  amountFor,
  buildRhythm,
  monthlyEquivalent,
  ordinal,
  repeatingTemplateTotal,
} from './rhythm';
import { scheduleToText } from './rhythmImport';
import { Cell, Row, S, SheetSpec, colName, downloadXlsx } from './xlsx';

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

type Group = 'Personal expenses' | 'Business expenses' | 'Recurring income';

/** Personal first — it's the owner's own sheet, and the household bills are
 *  what they open it to read. */
const GROUP_ORDER: Group[] = ['Personal expenses', 'Business expenses', 'Recurring income'];

function groupOf(direction: 'in' | 'out', scope: 'business' | 'personal'): Group {
  if (direction === 'in') return 'Recurring income';
  return scope === 'business' ? 'Business expenses' : 'Personal expenses';
}

// ---------------------------------------------------------------------------
// Columns — data columns only; the spacers are inserted when rows are written
// ---------------------------------------------------------------------------

const DATA_COLUMNS: Array<{ header: string; width: number }> = [
  { header: 'Bill:', width: 30 },
  { header: 'Amount:', width: 12 },
  { header: 'Date:', width: 17 },
  { header: 'Potentially:', width: 12 },
  { header: 'Per month:', width: 12 },
  { header: 'This period:', width: 13 },
  { header: 'Next due:', width: 14 },
  { header: 'Times:', width: 8 },
  { header: 'Type:', width: 19 },
  { header: 'Category:', width: 20 },
  { header: 'Books:', width: 10 },
  { header: 'Who:', width: 22 },
  { header: 'Source:', width: 18 },
  { header: 'Status:', width: 15 },
  { header: 'Notes:', width: 26 },
];

const SPACER_WIDTH = 2.5;

/** Data column n lives at sheet column 2n — A, C, E, G, … exactly as the
 *  owner's sheet is arranged. */
const sheetCol = (dataIndex: number) => dataIndex * 2;

const LAST_COL = sheetCol(DATA_COLUMNS.length - 1);

/** Spread data values across the spaced columns, leaving the gaps empty. */
function spaced(values: Array<Cell | null>): Row {
  const row: Row = [];
  values.forEach((cell, i) => {
    row[sheetCol(i)] = cell === null ? undefined : cell;
  });
  // A sparse array would leave holes; fill them so the writer sees real slots.
  for (let i = 0; i <= LAST_COL; i++) if (row[i] === undefined) row[i] = null;
  return row;
}

// Index of each data column, for formulas and for reading rows back in tests.
const COL = {
  bill: 0, amount: 1, date: 2, potentially: 3, perMonth: 4, thisPeriod: 5,
  nextDue: 6, times: 7, type: 8, category: 9, books: 10, who: 11,
  source: 12, status: 13, notes: 14,
} as const;

// ---------------------------------------------------------------------------

interface ItemRow {
  group: Group;
  name: string;
  amount: number | null;
  potentially: number | null;
  schedule: string;
  perMonth: number;
  thisPeriod: number;
  nextDue: Date | null;
  times: number | null;
  category: string;
  scope: 'business' | 'personal';
  who: string;
  source: MoneySource;
  status: string;
  notes: string;
}

/** 'YYYY-MM-DD' → local noon, so no timezone can move it a day before it
 *  becomes an Excel serial. */
function toDate(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
}

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatRange(start: string, end: string): string {
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  if (sy === ey && sm === em) return `${sd}–${ed} ${MONTHS_LONG[sm - 1]} ${sy}`;
  if (sy === ey) return `${sd} ${MONTHS_LONG[sm - 1]} – ${ed} ${MONTHS_LONG[em - 1]} ${sy}`;
  return `${sd} ${MONTHS_LONG[sm - 1]} ${sy} – ${ed} ${MONTHS_LONG[em - 1]} ${ey}`;
}

export interface WorkbookInput {
  bills?: import('./rhythm').RhythmInputs['bills'];
  asOf?: string;
  items: RecurringMoneyItem[];
  templates: RepeatingInvoice[];
  subscriptions: CrmSubscription[];
  rangeStart: string;
  rangeEnd: string;
  scenario: RhythmScenario;
  openingBalance: number;
  bufferLine: number | null;
  /** Ids muted on screen. Not excluded — reported in Status, so the sheet can
   *  explain any difference against what the owner is looking at. */
  mutedIds: Set<string>;
  generatedAt?: Date;
}

function indexBySource(occurrences: RhythmOccurrence[]): Map<string, RhythmOccurrence[]> {
  const map = new Map<string, RhythmOccurrence[]>();
  for (const o of occurrences) {
    const key = `${o.source}:${o.sourceId}`;
    const list = map.get(key);
    if (list) list.push(o);
    else map.set(key, [o]);
  }
  return map;
}

/**
 * Describe a business template's timing in the owner's own dialect.
 *
 * Their 'Date:' column says "1st" / "Every Friday" / "Monthly" — where in the
 * month a thing lands, not its abstract frequency. A monthly template is
 * therefore reported by the day it actually falls on, taken from its first
 * occurrence, so the column reads consistently down the whole sheet.
 */
function businessSchedule(frequency: string, firstOccurrence: string | null): string {
  const freq = String(frequency || '').toUpperCase();
  if (freq === 'MONTHLY' && firstOccurrence) return ordinal(Number(firstOccurrence.split('-')[2]));
  if (freq === 'WEEKLY') return 'Weekly';
  if (freq === 'QUARTERLY') return 'Every 3 months';
  if (freq === 'YEARLY') return 'Yearly';
  return freq ? freq.charAt(0) + freq.slice(1).toLowerCase() : 'Recurring';
}

function buildProjection(input: WorkbookInput) {
  return buildRhythm(
    { items: input.items, templates: input.templates, subscriptions: input.subscriptions, bills: input.bills, asOf: input.asOf },
    {
      rangeStart: input.rangeStart,
      rangeEnd: input.rangeEnd,
      scenario: input.scenario,
      directions: new Set(),
      scopes: new Set(),
      sources: new Set(),
      categories: new Set(),
      search: '',
      mutedIds: new Set(), // deliberately ignored — see the file header
      openingBalance: input.openingBalance,
    }
  );
}

function buildItemRows(input: WorkbookInput, bySource: Map<string, RhythmOccurrence[]>): ItemRow[] {
  const rows: ItemRow[] = [];

  // --- the household bills and any manual income ---------------------------
  for (const item of input.items) {
    const occ = bySource.get(`manual:${item.id}`) || [];
    rows.push({
      group: groupOf(item.direction, item.scope),
      name: item.name,
      amount: amountFor(item, input.scenario),
      // Blank rather than a repeat of Amount when there's no separate figure,
      // matching the source sheet where the column only differs sometimes.
      potentially: item.expectedAmount === undefined ? null : item.expectedAmount,
      schedule: scheduleToText(item),
      perMonth: item.active ? monthlyEquivalent(item, input.scenario) : 0,
      thisPeriod: occ.reduce((s, o) => s + o.amount, 0),
      nextDue: occ.length ? toDate(occ[0].date) : null,
      // A spread budget isn't "31 payments"; leave the count out rather than
      // print a number that invites the wrong conclusion.
      times: item.kind === 'monthly_spread' ? null : occ.length,
      category: getCategoryLabel(item.category),
      scope: item.scope,
      who: '',
      source: 'manual',
      status: !item.active
        ? 'Switched off'
        : input.mutedIds.has(item.id)
        ? 'Muted on screen'
        : 'Counted',
      notes: item.notes || '',
    });
  }

  // --- recurring invoice / bill templates -----------------------------------
  for (const template of input.templates) {
    for (const source of ['repeating_invoice', 'repeating_bill'] as const) {
      const occ = bySource.get(`${source}:${template.id}`);
      if (!occ || occ.length === 0) continue;
      const isBill = source === 'repeating_bill';
      const total = repeatingTemplateTotal(template);
      const perMonth =
        template.frequency === 'WEEKLY' ? (total * 52) / 12
        : template.frequency === 'QUARTERLY' ? total / 3
        : template.frequency === 'YEARLY' ? total / 12
        : total;
      rows.push({
        group: groupOf(isBill ? 'out' : 'in', 'business'),
        name: `${isBill ? 'Bill' : 'Invoice'}: ${template.contactName || 'Unknown'}`,
        amount: total,
        potentially: null,
        schedule: businessSchedule(template.frequency, occ[0].date),
        perMonth,
        thisPeriod: occ.reduce((s, o) => s + o.amount, 0),
        nextDue: toDate(occ[0].date),
        times: occ.length,
        category: getCategoryLabel(isBill ? 'expense:supplier' : 'income:customer_payment'),
        scope: 'business',
        who: template.contactName || '',
        source,
        status: 'Counted',
        notes: template.reference || '',
      });
    }
  }

  for (const bill of input.bills || []) {
    const occ = bySource.get('bill:' + bill.id) || [];
    if (!occ.length) continue;
    rows.push({ group: groupOf('out', 'business'), name: bill.billNumber + ': ' + bill.contactName,
      amount: bill.amountDue, potentially: null, schedule: 'Existing bill', perMonth: 0,
      thisPeriod: occ.reduce((n, o) => n + o.amount, 0), nextDue: toDate(occ[0].date), times: occ.length,
      category: getCategoryLabel('expense:supplier'), scope: 'business', who: bill.contactName || '',
      source: 'bill', status: 'Counted', notes: 'Remaining balance; payment not yet recorded' });
  }

  // --- Stripe plans ---------------------------------------------------------
  for (const sub of input.subscriptions) {
    const occ = bySource.get(`stripe_subscription:${sub.id}`);
    if (!occ || occ.length === 0) continue;
    const amount = Number(sub.currentAmount) || 0;
    const cycleMonths = sub.interval === 'year' ? 12 * (sub.intervalCount || 1) : sub.intervalCount || 1;
    rows.push({
      group: 'Recurring income',
      name: sub.planName || 'Stripe plan',
      amount,
      potentially: null,
      schedule:
        sub.interval === 'month' && (sub.intervalCount || 1) === 1
          ? ordinal(Number(occ[0].date.split('-')[2]))
          : `Every ${sub.intervalCount > 1 ? `${sub.intervalCount} ` : ''}${sub.interval}`,
      perMonth: cycleMonths > 0 ? amount / cycleMonths : amount,
      thisPeriod: occ.reduce((s, o) => s + o.amount, 0),
      nextDue: toDate(occ[0].date),
      times: occ.length,
      category: getCategoryLabel('income:customer_payment'),
      scope: 'business',
      who: sub.linkedCustomerName || sub.customerName || '',
      source: 'stripe_subscription',
      status: 'Counted',
      notes: sub.clientRef || '',
    });
  }

  return rows;
}

// ---------------------------------------------------------------------------
// Sheet assembly
// ---------------------------------------------------------------------------

export function buildMonthFlowSheet(input: WorkbookInput): SheetSpec {
  const generatedAt = input.generatedAt || new Date();
  const projection = buildProjection(input);
  const allItems = buildItemRows(input, indexBySource(projection.occurrences));

  const rows: Row[] = [];
  const merges: string[] = [];
  const blank = (): Row => [];

  const money = (v: number | null, band = false): Cell => ({ v, s: band ? S.moneyBand : S.money });
  const text = (v: string, band = false): Cell => ({ v, s: band ? S.textBand : S.text });
  const muted = (v: string, band = false): Cell => ({ v, s: band ? S.textBand : S.textMuted });

  // --- title ---------------------------------------------------------------
  rows.push([{ v: 'MONTH FLOW', s: S.title }]);
  merges.push(`A1:${colName(LAST_COL)}1`);

  rows.push([
    {
      v:
        `${formatRange(input.rangeStart, input.rangeEnd)} · ` +
        `${input.scenario === 'expected' ? 'realistic amounts' : 'full amounts'} · ` +
        `in ${fmtPlain(projection.totalIn)}, out ${fmtPlain(projection.totalOut)}, ` +
        `net ${fmtPlain(projection.net)}` +
        (projection.lowestBalanceDate
          ? ` · lowest ${fmtPlain(projection.lowestBalance ?? 0)} on ${formatShortDay(projection.lowestBalanceDate)}` +
            ` (from an opening ${fmtPlain(input.openingBalance)})`
          : '') +
        ` · generated ${generatedAt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`,
      s: S.subtitle,
    },
  ]);
  merges.push(`A2:${colName(LAST_COL)}2`);

  rows.push(blank());

  // --- header row ----------------------------------------------------------
  const headerRowIndex = rows.length;
  rows.push(spaced(DATA_COLUMNS.map((c) => ({ v: c.header, s: S.tableHeader }))));

  // --- one block per group -------------------------------------------------
  // Tracked in sheet coordinates (1-based) so the subtotal formulas can point
  // at the real cells rather than at a re-derived guess.
  const groupSumRanges: Array<{ first: number; last: number }> = [];

  for (const group of GROUP_ORDER) {
    const groupItems = allItems
      .filter((r) => r.group === group)
      .sort((a, b) => b.perMonth - a.perMonth);
    if (groupItems.length === 0) continue;

    rows.push(blank());

    const bannerIndex = rows.length;
    rows.push([{ v: group.toUpperCase(), s: S.sectionHeader }]);
    merges.push(`A${bannerIndex + 1}:${colName(LAST_COL)}${bannerIndex + 1}`);

    const firstDataRow = rows.length + 2; // +1 for the blank below, +1 for 1-based
    groupItems.forEach((r, i) => {
      // A blank row between every entry, exactly as the source sheet reads.
      rows.push(blank());
      const band = i % 2 === 1;
      rows.push(
        spaced([
          { v: r.name, s: band ? S.textBand : S.textBold },
          money(r.amount, band),
          text(r.schedule, band),
          money(r.potentially, band),
          money(r.perMonth, band),
          money(r.thisPeriod, band),
          { v: r.nextDue, s: band ? S.dateBand : S.date },
          { v: r.times, s: band ? S.integerBand : S.integer },
          text(r.group, band),
          text(r.category, band),
          text(SCOPE_LABELS[r.scope], band),
          muted(r.who, band),
          muted(SOURCE_LABELS[r.source], band),
          r.status === 'Counted' ? muted(r.status, band) : text(r.status, band),
          muted(r.notes, band),
        ])
      );
    });
    const lastDataRow = rows.length; // 1-based index of the row just written

    groupSumRanges.push({ first: firstDataRow, last: lastDataRow });

    // Subtotal. SUM spans the blank rows harmlessly — blanks are ignored —
    // which is what lets the spaced layout keep working formulas.
    rows.push(blank());
    rows.push(
      spaced([
        { v: `${group} subtotal`, s: S.textTotal },
        sumCell(COL.amount, firstDataRow, lastDataRow, groupItems.reduce((s, r) => s + (r.amount || 0), 0)),
        { v: '', s: S.textTotal },
        sumCell(COL.potentially, firstDataRow, lastDataRow, groupItems.reduce((s, r) => s + (r.potentially || 0), 0)),
        sumCell(COL.perMonth, firstDataRow, lastDataRow, groupItems.reduce((s, r) => s + r.perMonth, 0)),
        sumCell(COL.thisPeriod, firstDataRow, lastDataRow, groupItems.reduce((s, r) => s + r.thisPeriod, 0)),
        { v: '', s: S.textTotal },
        { v: `${groupItems.length} items`, s: S.textTotal },
        ...Array(7).fill({ v: '', s: S.textTotal }),
      ])
    );
  }

  // --- grand total ---------------------------------------------------------
  rows.push(blank());
  rows.push(blank());

  const grandTotal = (col: number, value: number): Cell =>
    groupSumRanges.length > 0
      ? {
          v: value,
          f: groupSumRanges.map((r) => `SUM(${colName(sheetCol(col))}${r.first}:${colName(sheetCol(col))}${r.last})`).join('+'),
          s: S.moneyTotal,
        }
      : { v: value, s: S.moneyTotal };

  rows.push(
    spaced([
      { v: 'TOTAL', s: S.textTotal },
      grandTotal(COL.amount, allItems.reduce((s, r) => s + (r.amount || 0), 0)),
      { v: '', s: S.textTotal },
      grandTotal(COL.potentially, allItems.reduce((s, r) => s + (r.potentially || 0), 0)),
      grandTotal(COL.perMonth, allItems.reduce((s, r) => s + r.perMonth, 0)),
      grandTotal(COL.thisPeriod, allItems.reduce((s, r) => s + r.thisPeriod, 0)),
      { v: '', s: S.textTotal },
      { v: `${allItems.length} items`, s: S.textTotal },
      ...Array(7).fill({ v: '', s: S.textTotal }),
    ])
  );

  // --- column widths -------------------------------------------------------
  const columnWidths: number[] = [];
  for (let i = 0; i <= LAST_COL; i++) {
    columnWidths[i] = i % 2 === 0 ? DATA_COLUMNS[i / 2].width : SPACER_WIDTH;
  }

  return {
    name: 'Month Flow',
    rows,
    columnWidths,
    merges,
    // No autofilter: the blank row between entries is the owner's layout, and a
    // filter over rows that alternate with blanks sorts them into nonsense.
    freezeAtRow: headerRowIndex + 1,
    printTitleRows: [headerRowIndex + 1, headerRowIndex + 1],
    landscape: true,
  };
}

/** SUM over one data column of a group, with the answer cached so it shows
 *  immediately (this writer emits no calcChain). */
function sumCell(col: number, firstRow: number, lastRow: number, value: number): Cell {
  const letter = colName(sheetCol(col));
  return { v: value, f: `SUM(${letter}${firstRow}:${letter}${lastRow})`, s: S.moneyTotal };
}

function fmtPlain(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

function formatShortDay(day: string): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [, m, d] = day.split('-').map(Number);
  return `${d} ${months[m - 1]}`;
}

/** Build the workbook and hand it to the browser. */
export function downloadMonthFlowWorkbook(input: WorkbookInput): string {
  const filename = `month-flow-${input.rangeStart}-to-${input.rangeEnd}.xlsx`;
  downloadXlsx(filename, buildMonthFlowSheet(input));
  return filename;
}

/** Exposed for tests — the column order is part of the contract with the
 *  owner's original sheet and shouldn't drift silently. */
export const EXPORT_COLUMNS = DATA_COLUMNS.map((c) => c.header);
export const EXPORT_COL_INDEX = COL;
export const sheetColumnFor = sheetCol;
